import { CouldNotConnectToServerException, UnknownNetworkException } from '../exceptions';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/switchMap';
import 'rxjs/add/operator/filter';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/merge';
import { ConnectionState, IConnectionStatus, IServerCommand, IServerConnection, IServerResponse, IServerConnectionLogger, TimeoutConfigCallback, IRetryInfo, ServerMessageType, IWebsocketConnectionOptions } from '../types';
import { WebSocketKeepAlive } from '../utilities';
import { IEventBusService } from '@/app-modules/event-bus/event-bus-service';
import { WebSocketUserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models';

const webSocketReadyStates = [
    'CONNECTING',
    'OPEN',
    'CLOSING',
    'CLOSED'
];
let nextConnectionId = 0;
let nextSocketId = 0;

export class WebSocketServerConnection implements IServerConnection {
    public stream$: Subject<IServerResponse>;
    public errorStatus$: Subject<Error>;
    public connectionStatus$: Subject<IConnectionStatus>;
    public error$: Subject<Error>;
    private socket: WebSocket;
    private messageBuffer: string;
    private connectionPromise: Promise<void>;
    private disconnectionPromise: Promise<void>;
    private keepAlive: WebSocketKeepAlive;
    private connectionStateValue: ConnectionState;
    private commandMessageBuffer: IServerCommand[] = [];
    private connectionIdValue: number;
    private socketIdValue: number;
    private webSocketUserSettingsUpdatedEventHandler: () => Promise<void>;
    private enableKeepAlive: boolean;
    private readonly format: ServerMessageType = 'xml';
    public readonly serverUrl: string;
    public readonly connectionLogger: IServerConnectionLogger;
    public readonly eventBusService: IEventBusService;
    public readonly partialMessages: boolean;

    // anything you add here will be stripped off the url for the web socket url - helped us fix when a room was called B!
    private nonDNSSafeCharacters: string[] = ['!'];

    public get readyState(): number {
        return this.socket ? this.socket.readyState : -1;
    }

    public get readyStateDescription(): string {
        return this.readyState < 0 ? 'UNDEFINED' : webSocketReadyStates[this.readyState];
    }

    public get connectionState(): ConnectionState {
        return this.connectionStateValue;
    }

    public get connectionId(): number {
        return this.connectionIdValue;
    }

    public get socketId(): number {
        return this.socketIdValue;
    }

    constructor(webSocketConnectionOptions: IWebsocketConnectionOptions) {
        this.serverUrl = webSocketConnectionOptions.serverUrl;
        this.stream$ = new Subject();
        this.errorStatus$ = new Subject();
        this.connectionStatus$ = new Subject();
        this.error$ = new Subject();
        this.keepAlive = new WebSocketKeepAlive(this, webSocketConnectionOptions.keepAliveConfigCallback, webSocketConnectionOptions.connectionLostConfigCallback, webSocketConnectionOptions.format);
        this.messageBuffer = '';
        this.connectionStateValue = ConnectionState.DISCONNECTED;
        this.connectionIdValue = ++nextConnectionId;
        this.connectionLogger = webSocketConnectionOptions.connectionLogger;
        this.eventBusService = webSocketConnectionOptions.eventBusService;
        this.partialMessages = webSocketConnectionOptions.partialMessages;
        this.webSocketUserSettingsUpdatedEventHandler = undefined;
        if (webSocketConnectionOptions.enableKeepAlive === undefined || webSocketConnectionOptions.enableKeepAlive === null){
            webSocketConnectionOptions.enableKeepAlive = true;
        }
        this.enableKeepAlive = webSocketConnectionOptions.enableKeepAlive;
    }

    public logger(): IServerConnectionLogger {
        return this.connectionLogger;
    }

    private getDNSSafeUrlFromNonSafeDNSUrl(nonSafeDNSUrl: string): string {
        return nonSafeDNSUrl.replace(new RegExp(this.nonDNSSafeCharacters.join('|'), 'g'), '');
    }

    public connect(retryInfo?: IRetryInfo): Promise<any> {
        if (this.readyState === WebSocket.CONNECTING || this.readyState === WebSocket.OPEN) {
            return this.connectionPromise;
        }

        if (this.readyState === WebSocket.CLOSING) {
            return Promise.reject('Connection currently closing');
        }

        this.connectionPromise = new Promise((resolve, reject) => {
            this.setConnectionState(ConnectionState.CONNECTING);
            this.broadcastNewConnectionState(ConnectionState.CONNECTING);

            try {
                const safeServerUrl = this.getDNSSafeUrlFromNonSafeDNSUrl(this.serverUrl);
                this.socket = new WebSocket(safeServerUrl);
                this.socketIdValue = ++nextSocketId;
                this.socket.onerror = () => { this.onError(this.getErrorStatus()); reject(); };
            } catch (err) {
                reject(err);
            }

            this.socket.onopen = () => {
                this.log('socket::open');

                this.socket.onmessage = (response) => this.onMessage(response);
                this.socket.onerror = () => this.onError(this.getErrorStatus());
                this.socket.onclose = () => this.onClose();

                this.setConnectionState(ConnectionState.CONNECTED);
                this.broadcastNewConnectionState(ConnectionState.CONNECTED);

                if (this.enableKeepAlive){
                    this.keepAlive.start(retryInfo);
                }

                this.unsubscribeWebSocketUserSettingsUpdatedEvent();
                this.subscribeWebSocketUserSettingsUpdatedEvent();

                resolve();
            };
        });

        return this.connectionPromise;
    }

    public send(command: IServerCommand): void {
        if (this.socket.readyState === WebSocket.OPEN) {
            this.sendOnSocket(command);
        } else {
            if (command.toPayload().includes('chat command=\"message\"') === true) {
                this.commandMessageBuffer.push(command);
            }
            this.onError(this.getErrorStatus());
        }
    }

    public disconnect(): Promise<void> {
        if (this.readyState === -1) {
            return Promise.resolve();
        }

        if (this.readyState === WebSocket.CLOSING || this.readyState === WebSocket.CLOSED) {
            return this.disconnectionPromise;
        }

        this.disconnectionPromise = new Promise((resolve, reject) => {
            try {
                this.unsubscribeWebSocketUserSettingsUpdatedEvent();

                this.setConnectionState(ConnectionState.DISCONNECTING);
                this.broadcastNewConnectionState(ConnectionState.DISCONNECTING);
                this.keepAlive.stop();
                this.socket.onclose = () => {
                    this.setConnectionState(ConnectionState.DISCONNECTED);
                    this.onClose();
                    resolve();
                };
                this.socket.close();
            } catch (err) {
                reject(err);
            }
        });

        return this.disconnectionPromise;
    }

    public sendOfflineMessageBuffer(): void {
        const bufferLength = this.commandMessageBuffer.length;
        if (bufferLength === 0) {
            return;
        }

        this.log(`offline-buffer::sending ${bufferLength} message(s)`);
        while (this.commandMessageBuffer.length > 0) {
            const message = this.commandMessageBuffer.shift();
            this.sendOnSocket(message);
        }
        this.log('offline-buffer::send complete');
    }

    private async handleWebSocketUserSettingsUpdatedEvent(): Promise<void> {
        this.log('keep-alive :: forced restart');
        this.keepAlive.stop();
        this.keepAlive.start();
    }

    private sendOnSocket(command: IServerCommand): void {
        const commandContent = command.toPayload();
        const contentLength = commandContent ? commandContent.length : 0;
        this.socket.send(commandContent);
        this.logDebug(`socket :: sent ${contentLength} bytes`);
    }

    private onMessage(event: MessageEvent): void {
        const dataLength = event.data ? ('' + event.data).length : 0;
        this.logDebug(`socket :: received ${dataLength} bytes`);

        if (this.partialMessages) {
            this.messageBuffer += event.data;
            const partialMessage = this.messageBuffer.substring(this.messageBuffer.lastIndexOf('\0'));
            const messages = this.messageBuffer.substring(0, this.messageBuffer.lastIndexOf('\0'));
            messages.split(/\0/).forEach((message) => {
                const response: IServerResponse = {
                    Contents: message.replace(/\0|\r?\n|\r/g, '')
                };
                if (response.Contents) {
                    this.stream$.next(response);
                }
            });
            this.messageBuffer = partialMessage;
        } else {
            this.stream$.next({
                Contents: event.data
            });
        }
    }

    private setConnectionState(state: ConnectionState): void {
        const prevState = this.connectionStateValue;
        this.connectionStateValue = state;
        this.log(`socket::connectionstatechange : state=${this.connectionStateValue}; prevstate=${prevState}`);
    }

    private broadcastNewConnectionState(state: ConnectionState) {
        this.log(`socket::broadcast-state : state=${state}`);
        this.connectionStatus$.next({ STATE: state });
    }

    private getErrorStatus(): Error {
        if (this.readyState !== WebSocket.OPEN) {
            return new CouldNotConnectToServerException();
        }

        return new UnknownNetworkException();
    }

    private onError(error: Error) {
        this.logError('socket::error', error);
        this.error$.next(error);
    }

    private onClose() {
        this.log('socket::close');
        this.reset();
        this.broadcastNewConnectionState(ConnectionState.DISCONNECTED);
    }

    private reset() {
        if (this.socket) {
            this.socket.onopen = null;
            this.socket.onerror = null;
            this.socket.onmessage = null;
            this.socket.onclose = null;

            this.log('socket::reset');
        }
    }

    public log(message: any) {
        this.connectionLogger.log(this.buildLogMessage(message), this.buildLogData());
    }

    public logDebug(message: any) {
        this.connectionLogger.debug(this.buildLogMessage(message), this.buildLogData());
    }

    public logError(message: any, error?: any) {
        const data = this.buildLogData();
        if (error) {
            data.error = error;
        }
        this.connectionLogger.error(this.buildLogMessage(message), data);
    }

    private buildLogMessage(message: any): string {
        const prefix = `WS[id=${this.connectionIdValue}; socket_id=${this.socketIdValue}; url=${this.serverUrl}; ready_state=${this.readyState}(${this.readyStateDescription})]: `;
        if (typeof message === 'string') {
            return `${prefix}${message}`;
        }
        return `${prefix}${JSON.stringify(message)}`;
    }

    private buildLogData(): any {
        return {
            url: this.serverUrl,
            connectionId : this.connectionIdValue,
            socketId: this.socketIdValue,
            socketReadyState: this.readyState
        };
    }

    private subscribeWebSocketUserSettingsUpdatedEvent(): void {
        this.webSocketUserSettingsUpdatedEventHandler = this.handleWebSocketUserSettingsUpdatedEvent.bind(this);
        this.eventBusService.getEventBus().$on(WebSocketUserSettingsUpdatedEventName, this.webSocketUserSettingsUpdatedEventHandler);
    }

    private unsubscribeWebSocketUserSettingsUpdatedEvent(): void {
        if (this.webSocketUserSettingsUpdatedEventHandler) {
            this.eventBusService.getEventBus().$off(WebSocketUserSettingsUpdatedEventName, this.webSocketUserSettingsUpdatedEventHandler);
            this.webSocketUserSettingsUpdatedEventHandler = undefined;
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/app-modules/server-communications/connection-types/web-socket.ts